/** The economic journal: complete, searchable by id, preserving receipt order and revisions (U061, U062). */
import { decodeJournalRow, type JournalEntry, type JournalRow } from "../../contracts/ops.ts";
import { badge, definitions, instantCell, metric } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import { formatExchangeDate } from "../../app/format.ts";
import { createTable } from "../../app/table.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { mountOps, panel } from "../ops-common.ts";
import { completeness, reduceJournal, searchEntries } from "./model.ts";

const ids = (entry: JournalEntry): string =>
  [
    ["decision", entry.decisionId],
    ["order", entry.orderId],
    ["command", entry.commandId],
    ["event", entry.eventId],
  ]
    .filter((pair): pair is [string, string] => pair[1] !== null)
    .map(([label, id]) => `${label} ${id}`)
    .join(", ");

/** Draw the journal: the completeness statement, the search box and the receipt-ordered timeline. */
export function drawJournal(rows: readonly JournalRow[]): readonly Child[] {
  const model = reduceJournal(rows);
  const state = completeness(model.summary);
  const table = createTable<JournalEntry>({
    label: "Journal timeline",
    rowKey: (entry) => entry.entryId,
    columns: [
      { key: "seq", header: "Receipt #", render: (entry) => String(entry.receiptSeq) },
      { key: "kind", header: "Kind", render: (entry) => badge(entry.entryKind, "info") },
      { key: "listing", header: "Listing", render: (entry) => metric(entry.listing) },
      { key: "ids", header: "Identifiers", render: (entry) => metric(ids(entry) || null) },
      { key: "summary", header: "Summary", render: (entry) => entry.summary },
      { key: "occurred", header: "Occurred", render: (entry) => instantCell(entry.occurredAt) },
      { key: "received", header: "Received", render: (entry) => instantCell(entry.receivedAt) },
      { key: "revision", header: "Revision", render: (entry) => String(entry.revision) },
    ],
  });
  const count = h("p", { attrs: { "aria-live": "polite" }, class: "summary" });
  const refresh = (query: string): void => {
    const shown = searchEntries(model.entries, query);
    table.setRows(shown);
    count.textContent = `Showing ${String(shown.length)} of ${String(model.entries.length)} loaded entries.`;
  };
  refresh("");
  const search = h(
    "label",
    { class: "search-box" },
    "Search the timeline by listing, decision, order, command or event ID ",
    h("input", {
      attrs: { type: "search", name: "journal-search", autocomplete: "off" },
      on: {
        input: (event) => {
          refresh((event.target as HTMLInputElement).value);
        },
      },
    }),
  );
  return [
    panel(
      "The day",
      "journal-day",
      model.summary === null
        ? null
        : definitions([
            ["Exchange date", formatExchangeDate(model.summary.exchangeDate)],
            ["Entries received", String(model.summary.totalEntries)],
            ["Zero-trade day", model.summary.zeroTradeDay ? "yes" : "no"],
            ["Unresolved exposure", model.summary.unresolvedExposure ? "yes" : "no"],
          ]),
      h(
        "p",
        {
          attrs: {
            role: state.complete ? "status" : "alert",
            "data-journal": state.complete ? "complete" : "incomplete",
          },
        },
        badge(state.complete ? "Complete" : "Not complete", state.complete ? "ok" : "bad"),
        ` ${state.text}`,
      ),
      h(
        "p",
        { class: "note" },
        "This timeline is never coalesced: every entry is kept in receipt order with its revision. Evidence exports are requested through the export jobs and keep their own scope and mode.",
      ),
    ),
    panel("Timeline", "journal-timeline", search, count, table.element),
  ];
}

/** Mount the journal. */
export function mountJournal(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "journal",
    title: "Journal and evidence",
    projection: "journal",
    row: decodeJournalRow,
    draw: (rows) => drawJournal(rows),
  });
}
